const CACHE_KEYS = {
  POST: 'C_POST',
  FILE: 'C_FILE',
  FILE_TALK_MSG_REQUESTED: 'C_FILE_TALK_MSG_REQUESTED',
  EVENT: 'C_EVENT',
  GROUP: 'C_GROUP',
  GROUPIDS: 'C_GROUPIDS',
  GROUPS_UPDATED_AT: 'C_GROUPS_UPDATED_AT',
  CM: 'C_CM',
  CMIDS: 'C_CMIDS',
  CMS_UPDATED_AT: 'C_CMS_UPDATED_AT',
  CM_INACTIVE_IDS: 'C_CM_INACTIVE_IDS',
  CMS_INVACTIVE_UPDATED_AT: 'C_CMS_INVACTIVE_UPDATED_AT',
  FRIEND_MAP: 'C_FM',
  TALK_MSGS: 'C_TMSS',
  TALK_OPTION_MSGS: 'C_OTMSS',
};

const WEEK = 60 * 24 * 7;

// { [key: number]: number }
const GROUPS_UPDATED_AT = {};

// { [key: number]: number }
const CMS_UPDATED_AT = {};

// { [key: number]: number }
const CMS_INACTIVE_UPDATED_AT = {};

export default class CacheUtils {
  static _set(key, obj, min) {
    min = min || WEEK;
    return new Promise((resolve, reject) => {
      try {
        $commin.clientdb.putObject(key, obj, min, result => {
          if (result) {
            resolve();
          } else {
            console.warn(`failed to save ${key}`);
            reject();
          }
        });
      } catch (e) {
        console.warn(`failed to get cache from key ${key}`);
        reject(e);
      }
    });
    $commin.clientdb.putObject(key, obj, min);
  }

  static _get(key) {
    return new Promise((resolve, reject) => {
      try {
        $commin.clientdb.getObject(key, data => {
          resolve(data);
        });
      } catch (e) {
        console.warn(`failed to get cache from key ${key}`);
        reject(e);
      }
    });
  }

  static _delete(key) {
    return new Promise((resolve, reject) => {
      try {
        $commin.clientdb.delete(key, () => {
          resolve(data);
        });
      } catch (e) {
        console.warn(`failed to delete cache key ${key}`);
        reject(e);
      }
    });
  }

  static async _setValue(key, v, min) {
    await CacheUtils._set(key, { v }, min);
  }

  static async _getValue(key, defaultValue) {
    const v = await CacheUtils._get(key);
    return v ? v.v : defaultValue;
  }

  static async setPost(pid, post) {
    await CacheUtils._set(`${CACHE_KEYS.POST}-${pid}`, post, WEEK);
  }

  static async getPost(pid) {
    return await CacheUtils._get(`${CACHE_KEYS.POST}-${pid}`);
  }

  static async setFile(fid, file) {
    await CacheUtils._set(`${CACHE_KEYS.FILE}-${fid}`, file, WEEK);
  }

  static async getFile(fid) {
    return await CacheUtils._get(`${CACHE_KEYS.FILE}-${fid}`);
  }

  static async setFileTalkMsgRequested(fids, time) {
    await CacheUtils._setValue(`${CACHE_KEYS.FILE_TALK_MSG_REQUESTED}-${fids}`, time, 60 * 3);
  }

  static async getFileTalkMsgRequested(fids) {
    return await CacheUtils._getValue(`${CACHE_KEYS.FILE_TALK_MSG_REQUESTED}-${fids}`, 0);
  }

  static async setEvent(eid, event) {
    await CacheUtils._set(`${CACHE_KEYS.EVENT}-${eid}`, event, WEEK);
  }

  static async getEvent(eid) {
    return await CacheUtils._get(`${CACHE_KEYS.EVENT}-${eid}`);
  }

  /**
    @param cid: number
    @param gs: Group[]
    @param updatedAt?: number
   */
  static async setGroups(cid, gs, updatedAt) {
    if (cid == 0) return console.warn(`cid=0 can't set groups.`);
    updatedAt = updatedAt || (await CacheUtils.getCmsUpdatedAt(cid));
    const gids = [];
    for (const g of gs) {
      gids.push(g.id);
      await CacheUtils.setGroup(g, updatedAt);
    }
    await CacheUtils.setGroupIds(cid, gids, updatedAt);
  }

  /**
    @param cid: number
    @param gids: number[]
    @param updatedAt?: number
   */
  static async setGroupIds(cid, gids, updatedAt) {
    if (cid == 0) return console.warn(`cid=0 can't set cms.`);
    const t = await CacheUtils.getGroupsUpdatedAt(cid);
    updatedAt = updatedAt || t;
    await CacheUtils._set(`${CACHE_KEYS.GROUPIDS}_${cid}`, JSON.stringify({ t: updatedAt, ids: gids }), WEEK);
    if (t == updatedAt) return;
    GROUPS_UPDATED_AT[cid] = t;
    await CacheUtils._set(`${CACHE_KEYS.GROUPS_UPDATED_AT}_${cid}`, updatedAt ? String(updatedAt) : '1', WEEK);
  }

  /**
    @param cid: number
    @return number[] | null
   */
  static async getGroupIds(cid) {
    if (cid == 0) {
      console.warn(`cid=0 does not have cmIds.`);
      return null;
    }
    const m = await CacheUtils._get(`${CACHE_KEYS.GROUPIDS}_${cid}`);
    if (!m) return null;
    const v = JSON.parse(m);
    if ((await CacheUtils.getGroupsUpdatedAt(cid)) < v.t) return null;
    return v.ids;
  }

  /**
    @param g: Group
    @param updatedAt?: number
   */
  static async setGroup(g, updatedAt) {
    updatedAt = updatedAt || (await CacheUtils.getCmsUpdatedAt(g.community_id));
    await CacheUtils._set(`${CACHE_KEYS.GROUP}_${g.id}`, JSON.stringify({ t: updatedAt, g }), WEEK);
  }

  /**
    @param gid: number
    @return Grous | null
   */
  static async getGroup(gid) {
    if (gid == 0) {
      console.warn(`gid=0 does not have group.`);
      return null;
    }
    const m = await CacheUtils._get(`${CACHE_KEYS.GROUP}_${gid}`);
    if (!m) return null;
    const v = JSON.parse(m);
    if ((await CacheUtils.getGroupsUpdatedAt(v.g.community_id)) < v.t) return null;
    return v.g;
  }

  /**
    @param cid: number
    @return number
   */
  static async getGroupsUpdatedAt(cid) {
    const v = GROUPS_UPDATED_AT[cid];
    if (v) return v;
    const m = await CacheUtils._get(`${CACHE_KEYS.GROUPS_UPDATED_AT}_${cid}`);
    const newV = m ? Number(m) : 0;
    GROUPS_UPDATED_AT[cid] = newV;
    return newV;
  }

  /**
    @param gid: number
   */
  static async deleteGroup(gid) {
    await CacheUtils._delete(`${CACHE_KEYS.GROUP}_${gid}`);
  }

  /**
    @param cid: number
    @param cms: Cm[]
    @param updatedAt?: number
   */
  static async setCms(cid, cms, updatedAt) {
    if (cid == 0) return console.warn(`cid=0 can't set cms.`);
    updatedAt = updatedAt || (await CacheUtils.getCmsUpdatedAt(cid));
    const cmids = [];
    for (const cm of cms) {
      cmids.push(cm.id);
      await CacheUtils.setCm(cm, updatedAt);
    }
    await CacheUtils.setCmIds(cid, cmids, updatedAt);
  }

  /**
    @param cid: number
    @param cmIds: number[]
    @param updatedAt?: number
   */
  static async setCmIds(cid, cmIds, updatedAt) {
    if (cid == 0) return console.warn(`cid=0 can't set cms.`);
    const t = await CacheUtils.getCmsUpdatedAt(cid);
    updatedAt = updatedAt || t;
    await CacheUtils._set(`${CACHE_KEYS.CMIDS}_${cid}`, JSON.stringify({ t: updatedAt, ids: cmIds }));
    if (t == updatedAt) return;
    CMS_UPDATED_AT[cid] = t;
    await CacheUtils._set(`${CACHE_KEYS.CMS_UPDATED_AT}_${cid}`, updatedAt ? String(updatedAt) : '1');
  }

  /**
    @param cid: number
    @return number[] | null
   */
  static async getCmIds(cid) {
    if (cid == 0) {
      console.warn(`cid=0 does not have cmIds.`);
      return null;
    }
    const m = await CacheUtils._get(`${CACHE_KEYS.CMIDS}_${cid}`);
    if (!m) return null;
    const v = JSON.parse(m);
    if ((await CacheUtils.getCmsUpdatedAt(cid)) < v.t) return null;
    return v.ids;
  }

  /**
    @param cmid: number
    @param cm: Cm
    @param updatedAt?: number
   */
  static async setCm(cmid, cm, updatedAt) {
    updatedAt = updatedAt || (await CacheUtils.getCmsUpdatedAt(cm.community_id));
    await CacheUtils._set(`${CACHE_KEYS.CM}_${cmid}`, JSON.stringify({ t: updatedAt, cm }));
  }

  /**
    @param cmid: number
    @return Cm | null
   */
  static async getCm(cmid) {
    if (cmid == 0) {
      console.warn(`cmid=0 does not have cm.`);
      return null;
    }
    const m = await CacheUtils._get(`${CACHE_KEYS.CM}_${cmid}`);
    if (!m) return null;
    const v = JSON.parse(m);
    if ((await CacheUtils.getCmsUpdatedAt(v.cm.community_id)) < v.t) return null;
    return v.cm;
  }

  /**
    @param cid: number
    @return number
   */
  static async getCmsUpdatedAt(cid) {
    const v = CMS_UPDATED_AT[cid];
    if (v) return v;
    const m = await CacheUtils._get(`${CACHE_KEYS.CMS_UPDATED_AT}_${cid}`);
    const newV = m ? Number(m) : 0;
    CMS_UPDATED_AT[cid] = newV;
    return newV;
  }

  /**
    @param cmid: number
   */
  static async deleteCm(cmid) {
    await CacheUtils._deleteByKey(`${CACHE_KEYS.CM}_${cmid}`);
  }

  /**
    @param cid: number
    @param cms: Cm[]
    @param updatedAt?: number
   */
  static async setInactiveCms(cid, cms, updatedAt) {
    if (cid == 0) return console.warn(`cid=0 can't set cms.`);
    updatedAt = updatedAt || (await CacheUtils.getInactiveCmsUpdatedAt(cid));
    const cmids = [];
    for (const cm of cms) {
      cmids.push(cm.id);
      await CacheUtils.setCm(cm, updatedAt);
    }
    await CacheUtils.setInactiveCmIds(cid, cmids, updatedAt);
  }

  /**
    @param cid: number
    @param cmIds: number[]
    @param updatedAt?: number
   */
  static async setInactiveCmIds(cid, cmIds, updatedAt) {
    if (cid == 0) return console.warn(`cid=0 can't set cms.`);
    const t = await CacheUtils.getInactiveCmsUpdatedAt(cid);
    updatedAt = updatedAt || t;
    await CacheUtils._set(`${CACHE_KEYS.CM_INACTIVE_IDS}_${cid}`, JSON.stringify({ t: updatedAt, ids: cmIds }));
    if (t == updatedAt) return;
    CMS_INACTIVE_UPDATED_AT[cid] = t;
    await CacheUtils._set(`${CACHE_KEYS.CMS_INVACTIVE_UPDATED_AT}_${cid}`, updatedAt ? String(updatedAt) : '1');
  }

  /**
    @param cid: number
    @return number[] | null
   */
  static async getInactiveCmIds(cid) {
    if (cid == 0) {
      console.warn(`cid=0 does not have cmIds.`);
      return null;
    }
    const m = await CacheUtils._get(`${CACHE_KEYS.CM_INACTIVE_IDS}_${cid}`);
    if (!m) return null;
    const v = JSON.parse(m);
    if ((await CacheUtils.getInactiveCmsUpdatedAt(cid)) < v.t) return null;
    return v.ids;
  }

  /**
    @param cid: number
    @return number
   */
  static async getInactiveCmsUpdatedAt(cid) {
    const v = CMS_INACTIVE_UPDATED_AT[cid];
    if (v) return v;
    const m = await CacheUtils._get(`${CACHE_KEYS.CMS_INVACTIVE_UPDATED_AT}_${cid}`);
    const newV = m ? Number(m) : 0;
    CMS_INACTIVE_UPDATED_AT[cid] = newV;
    return newV;
  }

  /**
    @param type: APP_TYPE
    @param id: number
    @param cmMap: { [key: number]: Cm }
   */
  static async setFriendMap(type, id, cmMap) {
    await CacheUtils._set(`${CACHE_KEYS.FRIEND_MAP}_${type}_${id}`, JSON.stringify({ map: cmMap, t: time() }), WEEK);
  }

  /**
    @param type: APP_TYPE
    @param id: number
    @return { map: { [key: number]: Cm }; t: number } | null
   */
  static async getFriendMap(type, id) {
    const m = await CacheUtils._get(`${CACHE_KEYS.FRIEND_MAP}_${type}_${id}`);
    return m ? JSON.parse(m) : null;
  }

  /**
    @param key: `msgs-${tid}-${i}`
    @param msgs: {[key:`m${num}`]:{[key:`c${msg.cmid}`]:TalkMsg[]}}
   */
  static async setTalkMsgs(key, msgs) {
    await CacheUtils._set(`${CACHE_KEYS.TALK_MSGS}_${key}`, JSON.stringify(msgs), WEEK);
  }

  /**
    @param key: `msgs-${tid}-${i}`
    @return {[key:`m${num}`]:{[key:`c${msg.cmid}`]:TalkMsg}} | null
   */
  static async getTalkMsgs(key) {
    const m = await CacheUtils._get(`${CACHE_KEYS.TALK_MSGS}_${key}`);
    return m ? JSON.parse(m) : null;
  }

  /**
    @param key: `${tid}_${num}`
    @param msgs: {[key:`${cmid}_${idx}`]:TalkMsg}
   */
  static async setTalkOptionMsgs(key, msgs) {
    await CacheUtils._set(`${CACHE_KEYS.TALK_OPTION_MSGS}_${key}`, JSON.stringify(msgs), WEEK);
  }

  /**
    @param key: `${tid}_${num}`
    @return {[key:`${cmid}_${idx}`]:TalkMsg} | null
   */
  static async getTalkOptionMsgs(key) {
    const m = await CacheUtils._get(`${CACHE_KEYS.TALK_OPTION_MSGS}_${key}`);
    return m ? JSON.parse(m) : null;
  }
}
